import React, { memo, useCallback, useMemo } from 'react';
import { path, pick } from 'ramda';
import { clsx } from '@core/js/utils/classNames.util';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';
import { Button } from '@theme/js/components/common/Button';
import { ReCaptcha } from '@core/js/components/main/ReCaptcha';
import { Label } from '@core/js/components/common/Fields/Label';
import { Radio } from '@core/js/components/common/Fields/Radio';
import { Input } from '@theme/js/components/common/Fields/Input';
import { Select } from '@theme/js/components/common/Fields/Select';
import { Checkbox } from '@theme/js/components/common/Fields/Checkbox';
import { DateInput } from '@core/js/components/common/Fields/DateInput';
import { useComponentTranslations, useComponentConfig } from '@core/js/hooks/component';
import { FORM_FIELD_TYPE, DATE_FIELD_DISPLAY_TYPE } from '@theme/js/components/common/Form/constants';
import { DatePicker, convertISOToPicker, convertPickerToISO } from '@theme/js/components/common/Fields/DatePicker';

import * as hooks from '@core/js/components/common/ChangeForm/components/ChangeFormField/hooks';
import { Limits } from '@core/js/components/common/ChangeForm/components/Limits';
import * as helpers from '@theme/js/components/common/ChangeForm/helpers';
import { IBANField } from '@core/js/components/common/ChangeForm/components/IBANField';
import { PhoneField } from '@core/js/components/common/ChangeForm/components/PhoneField';
import * as actions from '@core/js/components/common/ChangeForm/store/actions';
import { DepositLimits } from '@core/js/components/common/ChangeForm/components/DepositLimits';
import { NationalIdField } from '@core/js/components/common/ChangeForm/components/NationalIdField';
import { useDynamicReducerDispatch } from '@core/js/components/common/ChangeForm/store';
import { PlaceOfBirthField } from '@core/js/components/common/ChangeForm/components/PlaceOfBirthField';
import { ContactPreferences } from '@core/js/components/common/ChangeForm/components/ContactPreferences';
import { AddressLookupField } from '@core/js/components/common/ChangeForm/components/AddressLookupField';
import { PlaceOfResidenceField } from '@core/js/components/common/ChangeForm/components/PlaceOfResidenceField';
import { AddressLookupSwissField } from '@core/js/components/common/ChangeForm/components/AddressLookupSwissField';

import style from './ChangeFormField.scss';

export const ChangeFormField = memo(({
    field,
    disabled,
    formID = '',
    handleSubmit,
    isFieldHidden,
    setSubmitButton,
    onChange,
    className: classNameProp,
    classNames = {}, /* TotalCasino changes */
    disabledCustomField = false, /* TotalCasino changes */
    isPasswordForm = false, /* TotalCasino changes */
    isUnlimited = false // TotalCasino changes
}) => {
    const { componentId } = useComponentConfig();
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const { allowedCurrencies, allowedCountries } = useAppSelector(pick(['allowedCurrencies', 'allowedCountries']));
    const countryOfBirth = useUserSelector(path(['userDetails', 'countryOfBirth'])); /* TotalCasino changes */
    const isNeedToDisabledField = field.imsField === 'countryOfBirth' && disabledCustomField && Boolean(countryOfBirth); /* TotalCasino changes */

    const className = useMemo(() => clsx([
        classNameProp,
        isFieldHidden && 'hidden',
        `cms-form-field-name-${field.name}`
    ]), [classNameProp, isFieldHidden, field.name]);

    const {
        isReCaptchaVisible,
        onReCaptchaTokenValidate,
        onReCaptchaTokenError,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    } = hooks.useRecaptchaValidation();

    const internalValidate = hooks.useInternalValidate({
        field,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    const linkedPasswordFields = hooks.useLinkedPasswordFields(field.name);

    const {
        action,
        align,
        baseType,
        confirmationInfoTooltip,
        confirmationPlaceholder,
        customList = [],
        defaultValue,
        displayType = DATE_FIELD_DISPLAY_TYPE.SELECTOR,
        enableEscapeSymbols,
        escapeSymbolsRegExp,
        hideFromUser,
        id,
        infoTooltip,
        inputMode,
        name,
        placeholder,
        readOnly,
        requiresConfirmation,
        selectedList,
        type,
        validationRules
    } = field;
    // formID is needed because component can have different forms in preferences
    const fieldId = `${componentId}_${formID}_${id}`;
    const [label, getLabelText] = hooks.useFieldLabel(field.label);
    const [confirmationLabel, getConfirmationLabelText] = hooks.useFieldLabel(field.confirmationLabel);
    const [btnText] = hooks.useFieldLabel(field.label || 'playtech.mobile.system.form.submit.label');

    const {
        isLabelAsteriskEnabled,
        isPlaceholderAsteriskEnabled
    } = field.validation && validationRules?.find(rule => rule.type === 'required') || {};

    const countriesList = useMemo(() => helpers.getCountriesList(allowedCountries), [allowedCountries]);

    const currenciesList = useMemo(() => helpers.getCurrenciesList(allowedCurrencies), [allowedCurrencies]);

    const preventInput = useCallback((event) => event.preventDefault(), []);

    // this hook is needed to pass information that exactly this button was clicked
    const onSubmitClick = useCallback((event) => {
        setSubmitButton?.(field.name);
        handleSubmit?.(event);
    }, [field, setSubmitButton, handleSubmit]);

    if (hideFromUser) {
        return <input
            type="hidden"
            name={name}
            value={defaultValue}
        />;
    }

    switch (type) {
        case FORM_FIELD_TYPE.TEXT_LABEL:
            return <div className={`${style.textLabel} ${className}`} id={fieldId}>{label}</div>;
        case FORM_FIELD_TYPE.EMAIL:
        case FORM_FIELD_TYPE.USER_NAME:
            return (
                <>
                    <Input
                        id={fieldId}
                        name={name}
                        label={label}
                        type={baseType}
                        disabled={disabled}
                        onChange={onChange}
                        className={className}
                        /* TotalCasino changes -- passing classnames for customization */
                        classNames={classNames}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        placeholder={placeholder && translate(placeholder, placeholder)}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        aria-label={getLabelText() || translate([placeholder, 'playtech.mobile.system.input.aria-label'])}
                        {...readOnly && { readOnly: true }}
                        {...inputMode && { inputMode }}
                        {...internalValidate && {
                            validate: internalValidate,
                            validateFields: linkedPasswordFields,
                            // to rerender field with a new validateFields, it's needed to update another prop dependent from new validateFields
                            data: `${fieldId}_${linkedPasswordFields.join('-')}`
                        }}
                    />
                    {requiresConfirmation && (
                        <Input
                            type={type}
                            disabled={disabled}
                            className={className}
                            /* TotalCasino changes -- passing classnames for customization */
                            classNames={classNames}
                            label={confirmationLabel}
                            id={`${fieldId}_confirmation`}
                            name={`${name}_confirmation`}
                            isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                            isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                            placeholder={confirmationPlaceholder && translate(confirmationPlaceholder)}
                            infoTooltip={confirmationInfoTooltip && translate(confirmationInfoTooltip)}
                            aria-label={getConfirmationLabelText() || translate([confirmationPlaceholder, 'playtech.mobile.system.input.aria-label'])}
                        />
                    )}
                    {isReCaptchaVisible && !isFieldHidden ? (
                        <div className={style.captcha}>
                            <ReCaptcha
                                operation="checkUsernameAvailability"
                                onValidate={onReCaptchaTokenValidate}
                                onError={onReCaptchaTokenError}
                            />
                        </div>
                    ) : null}
                </>
            );
        case FORM_FIELD_TYPE.TEXT:
        case FORM_FIELD_TYPE.PASSWORD:
            return <>
                <Input
                    id={fieldId}
                    type={type}
                    name={name}
                    label={label}
                    disabled={disabled}
                    onChange={onChange}
                    className={className}
                    /* TotalCasino changes -- passing classnames for customization */
                    classNames={classNames}
                    isPasswordForm={isPasswordForm} /* TotalCasino changes */
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    placeholder={placeholder && translate(placeholder)}
                    infoTooltip={infoTooltip && translate(infoTooltip)}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    aria-label={getLabelText() || translate([placeholder, 'playtech.mobile.system.input.aria-label'])}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                    {...enableEscapeSymbols && { escapeSymbolsRegExp }}
                    {...type === FORM_FIELD_TYPE.PASSWORD && { validate: internalValidate }}
                />
                {
                    requiresConfirmation &&
                    <Input
                        {...type === FORM_FIELD_TYPE.PASSWORD && {
                            onPaste: preventInput,
                            onDrop: preventInput,
                            onCopy: preventInput,
                            onCut: preventInput,
                            onContextMenu: preventInput
                        }}
                        type={type}
                        disabled={disabled}
                        className={className}
                        /* TotalCasino changes -- passing classnames for customization */
                        classNames={classNames}
                        isPasswordForm={isPasswordForm} /* TotalCasino changes */
                        label={confirmationLabel}
                        name={`${name}_confirmation`}
                        id={`${fieldId}_confirmation`}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        placeholder={confirmationPlaceholder && translate(confirmationPlaceholder)}
                        infoTooltip={confirmationInfoTooltip && translate(confirmationInfoTooltip)}
                        aria-label={getConfirmationLabelText() || translate([confirmationPlaceholder, 'playtech.mobile.system.input.aria-label'])}
                    />
                }
            </>;
        case FORM_FIELD_TYPE.ADDRESS:
        case FORM_FIELD_TYPE.ADDRESS_2:
        case FORM_FIELD_TYPE.BUILDING_NUMBER:
        case FORM_FIELD_TYPE.HOUSE_NUMBER:
        case FORM_FIELD_TYPE.CITY:
        case FORM_FIELD_TYPE.PROPERTY_NUMBER:
        case FORM_FIELD_TYPE.PROVINCE:
        case FORM_FIELD_TYPE.STATE:
        case FORM_FIELD_TYPE.STREET_NAME:
        case FORM_FIELD_TYPE.SUBBUILDING:
        case FORM_FIELD_TYPE.UNIT_NUMBER:
        case FORM_FIELD_TYPE.ZIP:
            return (
                <Input
                    id={fieldId}
                    name={name}
                    label={label}
                    disabled={disabled}
                    onChange={onChange}
                    className={className}
                    /* TotalCasino changes -- passing classnames for customization */
                    classNames={classNames}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    placeholder={placeholder && translate(placeholder)}
                    infoTooltip={infoTooltip && translate(infoTooltip)}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    aria-label={getLabelText() || translate([placeholder, 'playtech.mobile.system.input.aria-label'])}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                />
        );

        case FORM_FIELD_TYPE.IBAN:
            return (
                <IBANField
                    name={name}
                    id={fieldId}
                    label={label}
                    disabled={disabled}
                    className={className}
                    validate={internalValidate}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    placeholder={placeholder && translate(placeholder)}
                    infoTooltip={infoTooltip && translate(infoTooltip)}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    ariaLabel={getLabelText() || translate([placeholder, 'playtech.mobile.system.input.aria-label'])}
                    {...readOnly && { readOnly: true }}
                />
            );
        case FORM_FIELD_TYPE.ADDRESS_LOOKUP:
            return (
                <AddressLookupField
                    field={field}
                    formID={formID}
                    fieldId={fieldId}
                    disabled={disabled}
                    className={classNameProp}
                    validate={internalValidate}
                    classNames={{ autocomplete: className }}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.ADDRESS_LOOKUP_SWISS:
            return (
                <AddressLookupSwissField
                    field={field}
                    formID={formID}
                    fieldId={fieldId}
                    disabled={disabled}
                    className={classNameProp}
                    validate={internalValidate}
                    classNames={{ autocomplete: className }}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.PLACE_OF_BIRTH:
            return (
                <PlaceOfBirthField
                    field={field}
                    formID={formID}
                    disabled={disabled}
                    className={classNameProp}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.PLACE_OF_RESIDENCE:
            return (
                <PlaceOfResidenceField
                    field={field}
                    formID={formID}
                    disabled={disabled}
                    className={classNameProp}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.ITALIAN_ID:
        case FORM_FIELD_TYPE.PORTUGAL_ID:
        case FORM_FIELD_TYPE.POLAND_ID:
        case FORM_FIELD_TYPE.ROMANIAN_ID:
        case FORM_FIELD_TYPE.SOUTH_AFRICAN_ID:
        case FORM_FIELD_TYPE.SPANISH_ID:
            return (
                <NationalIdField
                    id={fieldId}
                    type={type}
                    name={name}
                    label={label}
                    disabled={disabled}
                    className={className}
                    validate={internalValidate}
                    isFieldHidden={isFieldHidden}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    placeholder={placeholder && translate(placeholder)}
                    infoTooltip={infoTooltip && translate(infoTooltip)}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    ariaLabel={getLabelText() || translate([placeholder, 'playtech.mobile.system.input.aria-label'])}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                />
            );

        case FORM_FIELD_TYPE.CHECKBOX:
            return (
                <Checkbox
                    name={name}
                    id={fieldId}
                    label={label}
                    disabled={disabled}
                    className={className}
                    /* TotalCasino changes -- passing classnames for customization */
                    classNames={classNames}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    aria-label={getLabelText() || translate('playtech.mobile.system.checkbox.aria-label')}
                    {...readOnly && { readOnly: true }}
                />
            );

        case FORM_FIELD_TYPE.RADIO_BUTTON:
            return <>
                {(label || isLabelAsteriskEnabled) && (
                    <Label
                        id={fieldId}
                        label={label}
                        className={isFieldHidden ? 'hidden' : ''}
                        classNames={{ label: style.radioGroupLabel }}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                    />
                )}
                {customList.map((item) => (
                    <Radio
                        name={name}
                        key={item.id}
                        value={item.value}
                        disabled={disabled}
                        className={className}
                        label={item.key && translate(item.key)}
                        aria-label={translate([item.key, 'playtech.mobile.system.radio.aria-label'])}
                        {...readOnly && { readOnly: true }}
                    />
                ))}
            </>;

        case FORM_FIELD_TYPE.COUNTRY:
        case FORM_FIELD_TYPE.CURRENCY_CODE:
        case FORM_FIELD_TYPE.DROPDOWN:
        case FORM_FIELD_TYPE.BIRTH_PROVINCE:
        case FORM_FIELD_TYPE.CITY_OF_BIRTH:
        case FORM_FIELD_TYPE.COUNTRY_OF_BIRTH:
        case FORM_FIELD_TYPE.COUNTRY_OF_RESIDENCE:
        case FORM_FIELD_TYPE.CITY_OF_RESIDENCE:
        case FORM_FIELD_TYPE.PROVINCE_OF_RESIDENCE: {
            const selectedArray = (() => {
                switch (selectedList) {
                    case 'countries': return countriesList;
                    case 'currencies': return currenciesList;
                    default:
                        return customList.map(({
                            key,
                            value
                        }) => ({
                            label: translate(key, key),
                            value
                        }));
                }
            })();

            return (
                <Select
                    id={fieldId}
                    type={type}
                    name={name}
                    label={label}
                    disabled={isNeedToDisabledField || disabled} /* TotalCasino changes */
                    className={className}
                    /* TotalCasino changes -- passing classnames for customization */
                    classNames={classNames}
                    options={selectedArray}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    placeholder={placeholder && translate(placeholder)}
                    infoTooltip={infoTooltip && translate(infoTooltip)}
                    aria-label={getLabelText() || translate([placeholder, 'playtech.mobile.system.select.aria-label'])}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    {...readOnly && { readOnly: true }}
                    onChange={(event) => {
                        if (type === FORM_FIELD_TYPE.CURRENCY_CODE) {
                            dispatch(actions.setLimitsCurrency(event.target.value));
                        }

                        onChange?.(event);
                    }}
                />
            );
        }

        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER: {
            return displayType === DATE_FIELD_DISPLAY_TYPE.SELECTOR
                ? (
                    <DatePicker
                        name={name}
                        label={label}
                        disabled={disabled}
                        onChange={onChange}
                        className={className}
                        /* TotalCasino changes -- passing classnames for customization */
                        classNames={classNames}
                        parse={convertPickerToISO}
                        format={convertISOToPicker}
                        range={helpers.getRangeFromValidation(field)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                        {...readOnly && { readOnly: true }}
                        isUnlimited={isUnlimited} // TotalCasino changes
                    />
                ) : (
                    <DateInput
                        name={name}
                        id={fieldId}
                        label={label}
                        disabled={disabled}
                        onChange={onChange}
                        className={className}
                        /* TotalCasino changes -- passing classnames for customization */
                        classNames={classNames}
                        aria-label={getLabelText()}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        {...readOnly && { readOnly: true }}
                    />
                );
        }

        case FORM_FIELD_TYPE.BUTTON:
            return (
                <div className={clsx([className, style[align]])}>
                    <Button
                        key={id}
                        id={fieldId}
                        type="button"
                        disabled={disabled}
                        className={`${style.submit} ${classNames?.submit || ''}`} /* TotalCasino changes */
                        {...action === 'submit' && { onClick: onSubmitClick }}
                    >
                        {btnText}
                    </Button>
                </div>
            );

        case FORM_FIELD_TYPE.CELLPHONE:
        case FORM_FIELD_TYPE.PHONE:
            return (
                <PhoneField
                    field={field}
                    fieldId={fieldId}
                    disabled={disabled}
                    className={className}
                    validate={internalValidate}
                    isFieldHidden={isFieldHidden}
                    classNames={{ captcha: style.captcha }}
                    isReCaptchaVisible={isReCaptchaVisible}
                    linkedPasswordFields={linkedPasswordFields}
                    onReCaptchaTokenError={onReCaptchaTokenError}
                    onReCaptchaTokenValidate={onReCaptchaTokenValidate}
                />
            );

        case FORM_FIELD_TYPE.DEPOSIT_LIMITS: {
            return (
                <DepositLimits
                    field={field}
                    fieldId={fieldId}
                    className={className}
                />
            );
        }
        case FORM_FIELD_TYPE.BET_LIMITS:
        case FORM_FIELD_TYPE.LOSS_LIMITS: {
            return (
                <Limits
                    field={field}
                    fieldId={fieldId}
                    className={className}
                    type={field.type === FORM_FIELD_TYPE.BET_LIMITS ? 'bet' : 'loss'}
                />
            );
        }

        case FORM_FIELD_TYPE.CONTACT_PREFERENCES: {
            return (
                <ContactPreferences
                    className={className}
                    field={field}
                    fieldId={fieldId}
                />
            );
        }

        default:
            return null;
    }
});
